import { useEffect, useRef, useState } from 'react';

// --- O PAINEL DO TOPO QUE FECHA SOZINHO (01/09/2026, pedido do dono) --------
//
// O menu do usuário e o sino eram `<details>` nativos, e o nativo só sabe uma
// coisa: o clique abre, e só outro clique no MESMO botão fecha. Navegar não
// fechava, clicar fora não fechava — o painel ficava pendurado por cima da
// tela seguinte. Aqui o estado é nosso, e fechar tem quatro portas: o mouse
// sai, escolhe-se um item, clica-se fora, ou aperta-se Esc.
//
// O balcão é metade mouse, metade dedo. Aparelho de toque não tem "passar por
// cima" — e o mouse EMULADO do navegador dispararia a aproximação junto com o
// toque, abrindo e fechando no mesmo gesto. Por isso a aproximação só vale
// para `pointerType === 'mouse'`: com o dedo, o toque abre e fecha.

/**
 * O respiro antes de fechar quando o mouse sai. Não é enfeite: entre o botão
 * e o painel há a fresta de 4px do `mt-1`, e sem ele o menu sumia no meio do
 * caminho de quem estava indo escolher um item.
 */
export const RESPIRO_MS = 150;

export function usePainelSuspenso() {
  const [aberto, setAberto] = useState(false);
  const caixa = useRef<HTMLDivElement>(null);
  const respiro = useRef<number | null>(null);

  const cancelarRespiro = () => {
    if (respiro.current === null) return;
    window.clearTimeout(respiro.current);
    respiro.current = null;
  };

  const abrir = () => { cancelarRespiro(); setAberto(true); };
  const fechar = () => { cancelarRespiro(); setAberto(false); };
  const alternar = () => { if (aberto) fechar(); else abrir(); };

  // Só o mouse abre por aproximação; o dedo (e a caneta) abrem no toque.
  const aoAproximar = (e: { pointerType: string }) => {
    if (e.pointerType === 'mouse') abrir();
  };
  const aoAfastar = (e: { pointerType: string }) => {
    if (e.pointerType !== 'mouse') return;
    cancelarRespiro();
    respiro.current = window.setTimeout(() => setAberto(false), RESPIRO_MS);
  };

  // Clicar fora e Esc: as duas saídas de quem abriu no toque, onde não existe
  // "tirar o mouse de cima".
  useEffect(() => {
    if (!aberto) return;
    const foraDaqui = (e: Event) => {
      if (!caixa.current?.contains(e.target as Node)) setAberto(false);
    };
    const escapou = (e: KeyboardEvent) => { if (e.key === 'Escape') setAberto(false); };
    document.addEventListener('pointerdown', foraDaqui);
    document.addEventListener('keydown', escapou);
    return () => {
      document.removeEventListener('pointerdown', foraDaqui);
      document.removeEventListener('keydown', escapou);
    };
  }, [aberto]);

  // Trocar de tela com o respiro armado deixaria um timer mexendo em
  // componente já morto.
  useEffect(() => cancelarRespiro, []);

  return { aberto, caixa, abrir, fechar, alternar, aoAproximar, aoAfastar };
}
